import _ from 'lodash';
import moment from 'moment-timezone';

import { Timezone } from '@scale/shared/defaults';

// returns the same date in a different timezone
export const changeTimezone = (date: moment.Moment, timezone: Timezone): moment.Moment => {
  const dateUTC = date.clone().utc();

  if (timezone === Timezone.Local) return dateUTC.local();

  if (timezone === Timezone.Pacific) return dateUTC.tz('America/Los_Angeles');

  return dateUTC;
};

// turns a date object used to store day/month/year into a timezone aware Date
export const fromDateMonthYearToDate = (date: moment.Moment, timezone: Timezone): moment.Moment => {
  const dateMonthYear = [date.year(), date.month(), date.date()];
  if (timezone === Timezone.Local) {
    return moment(dateMonthYear);
  }
  if (timezone === Timezone.Pacific) {
    return moment.tz(dateMonthYear, 'America/Los_Angeles');
  }
  return moment.tz(dateMonthYear, 'UTC');
};

// returns a date with the same day/month/year but different timezone (so different point in time)
export function toDateMonthYear(date: moment.Moment): moment.Moment {
  return date.clone().tz('UTC', true);
}

type IDate = moment.Moment | undefined;

type QueryParam = string | (string | null)[] | null | undefined;

export const toUnix = _.memoize((date: IDate): QueryParam => {
  if (!date) return undefined;
  return `${moment(date).unix()}`;
});

export const fromUnix = _.memoize((timestamp: QueryParam) => {
  if (!timestamp) return undefined;
  if (Array.isArray(timestamp)) return undefined;

  return moment.unix(parseInt(timestamp, 10)).utc();
});

export const humanizeLatency = (value: number, durationType: any = 'seconds'): string => {
  if (durationType === 'milliseconds') {
    if (value < 1000) {
      return `${value.toFixed(1)}ms`;
    }

    return humanizeLatency((1.0 * value) / 1000, 'seconds');
  }

  if (durationType === 'seconds' && value < 60) {
    return `${value.toFixed(1)}s`;
  }

  return moment.duration(value, durationType).humanize();
};

export const getTimezoneLabel = (timezone?: Timezone) => {
  if (timezone === Timezone.UTC) return 'UTC';
  if (timezone === Timezone.Pacific) return 'PT';

  const timeOffset = new Date().getTimezoneOffset() / 60;
  const localGmt = timeOffset > 0 ? `-${timeOffset}` : `+${Math.abs(timeOffset)}`;

  return `GMT${localGmt}`;
};
